import React, { useRef, useEffect, useState } from 'react'
import { apiGet, getBackendUrl, apiPut, apiPost } from '../generic/Api_Functions'
import bcrypt from 'bcryptjs-react'
import JSEncrypt from 'jsencrypt'
import { Button, TextField, Paper, IconButton, InputAdornment, Box, Divider, CircularProgress } from '@mui/material'
import loginWallpaper from '../../images/login_wallpaper.jpg'
import ImageLogoDefault from '../../images/Logo.PNG'
import AlertUI from '../generic/AlertUI'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { Visibility, VisibilityOff, AccountCircle, Business } from '@mui/icons-material'
import { useUser } from '../generic/hooks/useUser'
import { useNavigate } from 'react-router-dom'
import { CheckCredentials } from '../generic/LoginFunctions'

function LoginPage ({ timeoutAlert }) {
  const [AlertElement, showAlert] = AlertUI()
  const { t_ } = useSmartTranslation()
  const showAlertRef = useRef(showAlert)
  const [showPasswordLogin, setShowPasswordLogin] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false)
  const [showNewPasswordDialog, setShowNewPasswordDialog] = useState(false)
  const [isLdapAuth, setIsLdapAuth] = useState(false)
  const { userLogin } = useUser()
  const navigate = useNavigate()
  const [loadingLogin, setLoadingLogin] = useState(false)

  const [ldapAvailable, setLdapAvailable] = useState(false)
  const [publicKey, setPublicKey] = useState(null)
  const [loadingLdapCheck, setLoadingLdapCheck] = useState(true)
  const [jsEncrypt] = useState(new JSEncrypt())

  // Form states
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    domain: '',
    newPassword: '',
    confirmPassword: ''
  })

  useEffect(() => {
    if (timeoutAlert) {
      showAlertRef.current({
        title: 'Timeout',
        severity: 'warning'
      })
    }
  }, [timeoutAlert])

  useEffect(() => {
    const checkLdapAvailability = async () => {
      try {
        setLoadingLdapCheck(true)
        const result = await fetch(getBackendUrl() + 'ldap/available')
        const response = await result.json()

        if (response) {
          setLdapAvailable(response.isAvailable || false)
          setIsLdapAuth(response.isAvailable || false)
          if (response.isAvailable && response.publicKey) {
            setPublicKey(response.publicKey.publicKey)
            jsEncrypt.setPublicKey(response.publicKey.publicKey)
          }
        }
      } catch (error) {
        setLdapAvailable(false)
        showAlert({
          title: 'Warning',
          severity: 'warning',
          message: t_('Could not verify LDAP availability')
        })
      } finally {
        setLoadingLdapCheck(false)
      }
    }

    checkLdapAvailability()
  }, [jsEncrypt, showAlert, t_])

  const backgroundImage = loginWallpaper

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundImage: `url(${backgroundImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    },
    logo: {
      width: '300px',
      marginBottom: '20px',
      objectFit: 'contain'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
      paddingLeft: '4rem',
      paddingRight: '4rem'
    },
    paper: {
      backgroundColor: '#303030',
      padding: '20px',
      borderRadius: '10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      maxWidth: '400px',
      width: '100%'
    },
    textField: {
      bgcolor: '303030',
      marginBottom: '20px',
      width: '100%'
    },
    authToggleContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: '20px',
      width: '100%'
    },
    authButton: {
      margin: '5px',
      minWidth: '200px'
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }
  }

  /**
   * Handle form data changes
   */
  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }))
  }

  /**
   * Clear form data
   */
  const clearForm = () => {
    setFormData({
      username: '',
      password: '',
      domain: '',
      newPassword: '',
      confirmPassword: ''
    })
  }

  const encryptPassword = (password) => {
    try {
      if (!publicKey) {
        throw new Error('Public Key not available')
      }

      const encryptedPassword = jsEncrypt.encrypt(password)

      if (!encryptedPassword) {
        throw new Error('Error encrypting password')
      }

      return encryptedPassword
    } catch (error) {
      throw new Error('Encryption Error: ' + error.message)
    }
  }

  /**
   * Handle submit of login form
   */
  const handleSubmit = async (event) => {
    event.preventDefault()
    if (loadingLogin) return
    setLoadingLogin(true)

    const { username, password, domain } = formData

    if (username && password) {
      try {
        if (isLdapAuth) {
          await checkLdapLogin(username, password, domain)
        } else {
          await checkLogin(username, password)
        }
      } finally {
        setLoadingLogin(false)
      }
    } else {
      setLoadingLogin(false)
      showAlert({
        title: 'Validation error',
        severity: 'warning',
        message: t_('Please fill in all required fields')
      })
    }
  }

  const showWrongCredentials = () => {
    showAlert({
      title: 'Login error',
      severity: 'warning',
      message: t_('Wrong username or password')
    })
  }

  /**
   * Check if is user's first login
   */
  const checkFirstLogin = async (username) => {
    const firstLogin = await apiGet('userFirstLogin/' + username)
    if (firstLogin) {
      setShowNewPasswordDialog(true)
    } else {
      userLogin()
      navigate('/')
    }
    return firstLogin
  }

  /**
   * Check username and password with DB,
   * if correct check if it's first login
   */
  const checkLogin = async (username, password) => {
    try {
      const dbUserPassword = await apiGet('userPassword/' + username)
      if (dbUserPassword && dbUserPassword.length !== 0) {
        await CheckCredentials(username, password, dbUserPassword)
        checkFirstLogin(username)
      } else {
        showWrongCredentials()
      }
    } catch (e) {
      showAlert({
        title: 'Login error',
        severity: e.name ? e.name : 'error',
        message: e.message ? e.message : ' '
      })
    }
  }

  /**
   * Check Windows Active Directory credentials
   */
  const checkLdapLogin = async (username, password, domain) => {
    try {
      if (!domain) {
        showAlert({
          title: 'Validation error',
          severity: 'warning',
          message: t_('Domain is required for LDAP authentication')
        })
        return
      }

      if (!ldapAvailable) {
        showAlert({
          title: 'Service unavailable',
          severity: 'error',
          message: t_('LDAP authentication is not available')
        })
        return
      }

      let encryptedPassword
      try {
        encryptedPassword = encryptPassword(password)
      } catch (encryptError) {
        showAlert({
          title: 'Encryption error',
          severity: 'error',
          message: t_('Error encrypting password:') + encryptError.message
        })
        return
      }

      const response = await apiPost('ldap', {
        domain,
        username,
        password: encryptedPassword
      })

      if (response && response.authenticated) {
        userLogin()
        navigate('/')
      } else {
        showAlert({
          title: 'Login error',
          severity: 'warning',
          message: response?.message || t_('LDAP authentication failed')
        })
      }
    } catch (e) {
      showAlert({
        title: 'Login error',
        severity: e.name ? e.name : 'error',
        message: e.message ? e.message : t_('LDAP authentication error')
      })
    }
  }

  /**
   * Toggle between Ldap and standard authentication
   */
  const toggleAuthMethod = (ldapAuth) => {
    setIsLdapAuth(ldapAuth)
    clearForm()
  }

  /**
   * Authentication method selector
   */
  const authMethodSelector = () => {
    if (loadingLdapCheck) {
      return (
        <Box sx={styles.authToggleContainer}>
          <Box sx={styles.loadingContainer}>
            <CircularProgress size={20} />
            <span style={{ marginLeft: '10px', color: '#aaa' }}>
              {t_('Checking authentication methods...')}
            </span>
          </Box>
          <Divider sx={{ width: '100%', margin: '15px 0', backgroundColor: '#555' }} />
        </Box>
      )
    }

    return (
      ldapAvailable && (
        <Box sx={styles.authToggleContainer}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Button
              variant={!isLdapAuth ? 'contained' : 'outlined'}
              color='primary'
              startIcon={<AccountCircle />}
              onClick={() => toggleAuthMethod(false)}
              sx={styles.authButton}
            >
              MIC
            </Button>
            <Button
              variant={isLdapAuth ? 'contained' : 'outlined'}
              color='primary'
              startIcon={<Business />}
              onClick={() => toggleAuthMethod(true)}
              sx={styles.authButton}
            >
              Windows AD
            </Button>
          </Box>
          <Divider sx={{ width: '100%', margin: '15px 0', backgroundColor: '#555' }} />
        </Box>
      )
    )
  }
  /**
   * Form dialog for login
   */
  const loginForm = () => {
    return (
      <form onSubmit={handleSubmit} style={styles.form}>
        {authMethodSelector()}

        {isLdapAuth && ldapAvailable && (
          <>
            <TextField
              color='primary'
              label={t_('Domain')}
              placeholder='DOMAIN'
              value={formData.domain}
              onChange={handleInputChange('domain')}
              required
              variant='filled'
              sx={styles.textField}
            />
          </>
        )}

        <TextField
          color='primary'
          label={isLdapAuth ? t_('Username') : t_('User')}
          placeholder={isLdapAuth ? 'username' : ''}
          value={formData.username}
          onChange={handleInputChange('username')}
          required
          variant='filled'
          sx={styles.textField}
        />

        <TextField
          color='primary'
          type={showPasswordLogin ? 'text' : 'password'}
          label={t_('Password')}
          value={formData.password}
          onChange={handleInputChange('password')}
          required
          variant='filled'
          sx={styles.textField}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='visibility'
                  onClick={() => setShowPasswordLogin(!showPasswordLogin)}
                  edge='end'
                >
                  {showPasswordLogin ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {AlertElement && <div style={{ marginBottom: '10px' }}>{AlertElement}</div>}

        <Button
          type='submit'
          variant='contained'
          color='primary'
          disabled={loadingLogin || (isLdapAuth && !ldapAvailable)}
          sx={{ minWidth: '200px' }}
        >
          {loadingLogin
            ? t_('Authenticating...')
            : t_('Log in')}
        </Button>
      </form>
    )
  }

  /**
   * Handle password validation
   */
  const handleCheckPassword = (pwd1, pwd2) => {
    if (pwd1 !== pwd2) {
      showAlert({
        title: 'Password error',
        severity: 'warning',
        message: t_('Passwords do not match')
      })
      return false
    } else if (pwd1.length < 5 || pwd2.length < 5) {
      showAlert({
        title: 'Password error',
        severity: 'warning',
        message: t_('Password must have at least 5 characters')
      })
      return false
    } else {
      return true
    }
  }

  /**
   * Handle save new password
   */
  const handleSaveNewPassword = (password) => {
    const salt = bcrypt.genSaltSync(10)
    const hashedPassword = bcrypt.hashSync(password, salt)

    apiPut('user/setMyPassword', {
      username: formData.username,
      hashedPassword,
      challenge: null,
      salt
    })
      .then(() => {
        setShowNewPasswordDialog(false)
        showAlert({ severity: 'success', title: t_('Password set successfully') })
      })
      .catch(() => {
        showAlert({ severity: 'error', title: t_('Error while setting password') })
      })
  }

  /**
   * Handle submit new password
   */
  const handleSubmitNewPassword = (event) => {
    event.preventDefault()
    const { newPassword, confirmPassword } = formData

    if (handleCheckPassword(newPassword, confirmPassword)) {
      handleSaveNewPassword(newPassword)
    }
  }

  /**
   * Form dialog for creating a new password
   */
  const newPasswordForm = () => {
    return (
      <form onSubmit={handleSubmitNewPassword} style={styles.form}>
        <h3>{t_('Create new password')}</h3>
        <TextField
          color='primary'
          type={showPassword ? 'text' : 'password'}
          label={t_('Password')}
          value={formData.newPassword}
          onChange={handleInputChange('newPassword')}
          required
          variant='filled'
          sx={styles.textField}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='visibility'
                  onClick={() => setShowPassword(!showPassword)}
                  edge='end'
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <TextField
          color='primary'
          type={showPasswordConfirm ? 'text' : 'password'}
          label={t_('Confirm Password')}
          value={formData.confirmPassword}
          onChange={handleInputChange('confirmPassword')}
          required
          variant='filled'
          sx={styles.textField}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  aria-label='visibility'
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  edge='end'
                >
                  {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        {AlertElement && <div style={{ marginBottom: '10px' }}>{AlertElement}</div>}
        <div>
          <Button
            sx={{ marginInline: '0.5rem' }}
            type='submit'
            variant='contained'
            color='primary'
          >
            {t_('Save')}
          </Button>
          <Button
            sx={{ marginInline: '0.5rem' }}
            variant='contained'
            color='primary'
            onClick={() => {
              setShowNewPasswordDialog(false)
              clearForm()
            }}
          >
            {t_('Cancel')}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <div style={styles.container}>
      <Paper sx={styles.paper}>
        <img
          src={getBackendUrl() + 'logo'}
          height='100px'
          alt='SmartPM logo'
          style={styles.logo}
          onError={(e) => { e.target.src = ImageLogoDefault }}
        />
        {!showNewPasswordDialog ? loginForm() : newPasswordForm()}
      </Paper>
    </div>
  )
}

export default LoginPage
